import { useEffect, useState, useCallback } from "react";
import { TextField, Dialog, DialogActions, DialogContent, DialogTitle, Button, Alert, Grid, MenuItem, Divider, Chip, Autocomplete, Checkbox } from "@mui/material";
import { useAuth } from "../../../hooks/useAuth";
import { GetTokenIdentity } from "../../../utils/Token";
import { GroupHeader, GroupItems } from "../../../utils/Styled";
import { Countries } from "../../../utils/Lookup";
import { Icons } from "../../../utils/Icon";

import _ from "lodash";
import axios from "axios";
import Swal from "sweetalert2";

export default function CreateOrganizationForm({ open, handleClose, fetchData, userCountry }) {
    const { token } = useAuth();
    const identity = GetTokenIdentity(token);

    const [newData, setNewData] = useState({});

    const [errors, setErrors] = useState({ org_code: false, org_name: false, country_id: false });
    const [helperText, setHelperText] = useState({});
    const [errorMessage, setErrorMessage] = useState("");

    const [productsData, setProductsData] = useState([]);
    const [businessTypeData, setBusinessTypeData] = useState([]);
    const [codeData, setCodeData] = useState([]);
    const [nameData, setNameData] = useState([]);

    const [userBusinessTypeInput, setUserBusinessTypeInput] = useState("");
    const [userCodeInput, setUserCodeInput] = useState("");
    const [userNameInput, setUserNameInput] = useState("");

    const setError = async (field, message) => {
        await setErrors(prevErrors => ({ ...prevErrors, [field]: true }));
        await setHelperText(prevHelperText => ({ ...prevHelperText, [field]: message }));
    };

    const validate = async (field, value) => {
        if (field === "org_code" && value === "") {
            await setError("org_code", "Code is required.");
        } else if (field === "org_name" && value === "") {
            await setError("org_name", "Name is required.");
        } else if (field === "country_id" && value === "") {
            await setError("country_id", "Manage By is required.");
        } else {
            await setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
            await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: "" }));
        }
    };

    const fetchBusinessTypeData = useCallback(async () => {
        try {
            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/organizations/get-business-type?search=${userBusinessTypeInput}`);
            setBusinessTypeData(response.data.data.texts);
        } catch (error) {
            console.error(error);
        }
    }, [userBusinessTypeInput]);
    
    useEffect(() => {
        fetchBusinessTypeData();
    }, [fetchBusinessTypeData]);

    const fetchCodeData = useCallback(async () => {
        try {
            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/organizations/get-code?search=${userCodeInput}&country=${newData.country_id}`);
            setCodeData(response.data.data.texts);
        } catch (error) {
            console.error(error);
        }
    }, [userCodeInput, newData.country_id]);
    
    useEffect(() => {
        fetchCodeData();
    }, [fetchCodeData]);

    const fetchNameData = useCallback(async () => {
        try {
            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/organizations/get-name?search=${userNameInput}&country=${newData.country_id}`);
            setNameData(response.data.data.texts);
        } catch (error) {
            console.error(error);
        }
    }, [userNameInput, newData.country_id]);
    
    useEffect(() => {
        fetchNameData();
    }, [fetchNameData]);

    useEffect(() => {
        if (open) {
            setUserBusinessTypeInput("");
            setUserCodeInput("");
            setUserNameInput("");
            setNewData({ org_code: "", org_name: "", address: "", postal_code: "", city: "", state: "", country_id: _.findIndex(userCountry, { country_id: identity.user.country_id }) < 0 ? "" : identity.user.country_id, email: "", emailCc: "", office_phone: "", mobile_phone: "", pic_name: "", business_type: "", remarks: "", product_id: [] });
            setErrors({ org_code: false, org_name: false, country_id: false });
            setHelperText({});
            setErrorMessage("");

            axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/products/select")
                .then((response) => setProductsData(response.data.data.products))
                .catch((error) => console.log(error));
        }
    }, [open, identity.user.country_id, userCountry]);

    const handleCreateProduct = async () => {
        let isValid = true;

        if (newData["org_code"] === "") isValid = false;
        if (newData["org_name"] === "") isValid = false;
        if (newData["country_id"] === "") isValid = false;

        Object.keys(newData).forEach(async (field) => {
            await validate(field, newData[field]);
        });

        if (isValid) {
            await setErrorMessage("");
            await createOrganization();
        } else {
            await setErrorMessage(`Please fill all the required fields.`);
        }
    };

    const createOrganization = async () => {
        try {
            const { org_code, org_name, address, postal_code, city, state, country_id, email, emailCc, office_phone, mobile_phone, pic_name, business_type, remarks, product_id } = newData;
            const selected_products = product_id.map((product) => product.product_id);
            const response = await axios.post(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/organizations/create", {
                org_code, org_name, address, postal_code, city, state, country_id, email, emailCc, office_phone, mobile_phone, pic_name, business_type, remarks, selected_products
            });

            if (response.status === 201) {
                handleClose();
                Swal.fire({
                    title: "Success",
                    text: "Organization created successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred while creating the organization. Please try again later.");
            }
        }
    };

    return (
        <Dialog
            maxWidth="lg"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Create New Organization</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <Divider style={{ paddingTop: "0px", paddingBottom: "4px" }}>
                    <Chip label="Organization Details" />
                </Divider>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <Autocomplete
                            freeSolo
                            disableClearable
                            options={codeData}
                            getOptionLabel={(option) => option.org_code || ''}
                            inputValue={userCodeInput}
                            onInputChange={(event, newInputValue) => {
                                setUserCodeInput(newInputValue.toUpperCase());
                                setNewData({ ...newData, org_code: newInputValue ? newInputValue.toUpperCase() : '' });
                                validate('org_code', newInputValue.toUpperCase());
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    autoFocus
                                    margin="dense"
                                    label="Code"
                                    fullWidth
                                    required
                                    error={errors.org_code}
                                    helperText={helperText.org_code}
                                    InputProps={{
                                        ...params.InputProps, type: 'search',
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={9}>
                        <Autocomplete
                            freeSolo
                            disableClearable
                            options={nameData}
                            getOptionLabel={(option) => option.org_name || ''}
                            inputValue={userNameInput}
                            onInputChange={(event, newInputValue) => {
                                setUserNameInput(newInputValue.toUpperCase());
                                setNewData({ ...newData, org_name: newInputValue ? newInputValue.toUpperCase() : '' });
                                validate('org_name', newInputValue.toUpperCase());
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    margin="dense"
                                    label="Name"
                                    fullWidth
                                    error={errors.org_name}
                                    helperText={helperText.org_name}
                                    InputProps={{
                                        ...params.InputProps, type: 'search',
                                    }}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="email"
                            label="Email"
                            type="text"
                            fullWidth
                            error={errors.email}
                            helperText={helperText.email}
                            onChange={(e) => {
                                setNewData({ ...newData, email: e.target.value, });
                                validate("email", e.target.value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="emailCc"
                            label="Email CC"
                            type="text"
                            fullWidth
                            error={errors.emailCc}
                            helperText={helperText.emailCc}
                            onChange={(e) => {
                                setNewData({ ...newData, emailCc: e.target.value, });
                                validate("emailCc", e.target.value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="office_phone"
                            label="Office Phone"
                            type="text"
                            defaultValue=""
                            fullWidth
                            error={errors.office_phone}
                            helperText={helperText.office_phone}
                            onChange={(e) => {
                                setNewData({ ...newData, office_phone: e.target.value, });
                                validate("office_phone", e.target.value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="mobile_phone"
                            label="Mobile Phone"
                            type="text"
                            defaultValue=""
                            fullWidth
                            error={errors.mobile_phone}
                            helperText={helperText.mobile_phone}
                            onChange={(e) => {
                                setNewData({ ...newData, mobile_phone: e.target.value, });
                                validate("mobile_phone", e.target.value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="pic_name"
                            label="PIC_Name"
                            type="text"
                            fullWidth
                            error={errors.pic_name}
                            helperText={helperText.pic_name}
                            onChange={(e) => {
                                setNewData({ ...newData, pic_name: e.target.value, });
                                validate("pic_name", e.target.value);
                            }}
                        />
                    </Grid>
                </Grid>
                <Divider style={{ paddingTop: "16px", paddingBottom: "4px" }}>
                    <Chip label="Address Details" />
                </Divider>
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="address"
                    label="Address"
                    type="text"
                    fullWidth
                    error={errors.address}
                    helperText={helperText.address}
                    onChange={(e) => {
                        setNewData({ ...newData, address: e.target.value, });
                        validate("address", e.target.value);
                    }}
                />
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <TextField
                            margin="dense"
                            autoComplete="off"
                            id="city"
                            label="City"
                            type="text"
                            defaultValue=""
                            fullWidth
                            error={errors.city}
                            helperText={helperText.city}
                            onChange={(e) => {
                                setNewData({ ...newData, city: e.target.value, });
                                validate("city", e.target.value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="state"
                            label="State"
                            type="text"
                            defaultValue=""
                            fullWidth
                            error={errors.state}
                            helperText={helperText.state}
                            onChange={(e) => {
                                setNewData({ ...newData, state: e.target.value, });
                                validate("state", e.target.value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="postal_code"
                            label="Postal Code"
                            type="text"
                            defaultValue=""
                            fullWidth
                            error={errors.postal_code}
                            helperText={helperText.postal_code}
                            onChange={(e) => {
                                setNewData({ ...newData, postal_code: e.target.value, });
                                validate("postal_code", e.target.value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <Autocomplete
                            freeSolo
                            disableClearable
                            options={businessTypeData}
                            getOptionLabel={(option) => option.business_type || ''}
                            inputValue={userBusinessTypeInput}
                            onInputChange={(event, newInputValue) => {
                                setUserBusinessTypeInput(newInputValue.toUpperCase());
                                setNewData({ ...newData, business_type: newInputValue ? newInputValue.toUpperCase() : '' });
                                validate('business_type', newInputValue.toUpperCase());
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    margin="dense"
                                    label="Nature Of Business"
                                    fullWidth
                                    error={errors.business_type}
                                    helperText={helperText.business_type}
                                    InputProps={{
                                        ...params.InputProps, type: 'search',
                                    }}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                <Divider style={{ paddingTop: "16px", paddingBottom: "4px" }}>
                    <Chip label="Product Existing Details" />
                </Divider>
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={productsData}
                    groupBy={(option) => {
                        const country = Countries.find((country) => country.id === option.country_id);
                        return country ? country.name : "Unknown";
                    }}
                    getOptionLabel={(option) => option.product_description}
                    onChange={(event, value) => {
                        setNewData({
                            ...newData,
                            product_id: value ? value : [],
                        });
                    }}
                    renderOption={(props, option, { selected }) => (
                        <li {...props}>
                            <Checkbox icon={Icons.CheckBoxBlankIcon} checkedIcon={Icons.CheckBoxCheckedIcon} style={{ marginRight: 8 }} checked={selected} />
                            {option.product_description}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} margin="dense" id="product_id" label="Product" fullWidth error={errors.product_id} helperText={helperText.product_id} />}
                    renderGroup={(params) => {
                        return (
                            <li key={params.key}>
                                <GroupHeader>{params.group}</GroupHeader>
                                <GroupItems>{params.children}</GroupItems>
                            </li>
                        );
                    }}
                />
                <Divider style={{ paddingTop: "16px", paddingBottom: "4px" }}>
                    <Chip label="Additional Details" />
                </Divider>
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="remarks"
                    label="Remark"
                    type="text"
                    multiline
                    rows={3}
                    fullWidth
                    error={errors.remarks}
                    helperText={helperText.remarks}
                    onChange={(e) => {
                        setNewData({ ...newData, remarks: e.target.value, });
                        validate("remarks", e.target.value);
                    }}
                />
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="country_id"
                            select
                            label="Manage By"
                            type="number"
                            defaultValue={_.findIndex(userCountry, { country_id: identity.user.country_id }) < 0 ? "" : identity.user.country_id }
                            fullWidth
                            required
                            error={errors.country_id}
                            helperText={helperText.country_id}
                            onChange={(e) => {
                                setNewData({ ...newData, country_id: e.target.value, });
                                validate("country_id", e.target.value);
                            }}
                        >
                            {userCountry.map((country) => (
                                <MenuItem key={country.country_id} value={country.country_id}>
                                    {country.country_name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleCreateProduct} variant="contained" color="primary" style={{ marginRight: "6px", color: "white" }}>
                    Create
                </Button>
                <Button onClick={handleClose} variant="contained" color="error" style={{ marginRight: "6px", color: "white" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
